/**
 * Settings → Tracking. How the attribution numbers are made (in three plain
 * sentences), whether the tracking is healthy, and the conversion sync that
 * sends signups, trial starts and payments back to the ad platforms.
 *
 * Backend: api/src/routes/tracking.ts + api/src/lib/command/conversions.ts.
 */
import { useCallback, useEffect, useState } from "react";
import { Activity, Eye, Footprints, Link2, MousePointerClick, Plus, Send as SendIcon, Target, Users } from "lucide-react";
import { Button, Skeleton, useToast } from "@/components/ui";
import { Card, Empty, Section, Stat, num } from "@/features/command/ui";
import { errText, trackingApi, type Destination, type TrackingData } from "./api";
import { AddDestinationModal, DestinationRow } from "./Destinations";
import { SendsLog } from "./SendsLog";

const HOW = [
  { icon: Eye, text: "Every visit to syncsuite.co is recorded: the page, where the visitor came from, and any ad they clicked." },
  { icon: Link2, text: "When someone signs up, we link their earlier visits, ad clicks and referral code to them." },
  { icon: Target, text: "Each customer is credited to one traffic source: a referral code wins, otherwise the last place they came from, otherwise the first." },
];

export function TrackingTab() {
  const toast = useToast();
  const [data, setData] = useState<TrackingData | null>(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await trackingApi.load());
      setFailed(false);
    } catch (err) {
      setFailed(true);
      toast.error("Couldn't load tracking", errText(err));
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const setDestinations = (fn: (list: Destination[]) => Destination[]) =>
    setData((x) => (x ? { ...x, destinations: fn(x.destinations) } : x));

  if (!data) {
    if (failed) {
      return (
        <Card className="lg:max-w-4xl">
          <Empty icon={Activity} title="Couldn't load tracking" action={<Button variant="secondary" onClick={() => void load()}>Try again</Button>} />
        </Card>
      );
    }
    return (
      <div className="space-y-4 lg:max-w-4xl">
        <Skeleton className="h-36" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  const h = data.health;
  const people = h.credited + h.uncredited;
  const creditedShare = people > 0 ? h.credited / people : 0;

  return (
    <div className="space-y-5 lg:max-w-4xl">
      {/* How it works */}
      <Section title="How tracking works" icon={Footprints}>
        <ol className="space-y-3">
          {HOW.map(({ icon: Icon, text }, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/20">
                <Icon className="h-3.5 w-3.5 text-indigo-300" />
              </span>
              <p className="pt-1 text-sm leading-relaxed text-zinc-300">{text}</p>
            </li>
          ))}
        </ol>
        <details className="group mt-4 rounded-xl border border-zinc-800/70 px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium text-zinc-400 hover:text-zinc-200">The technical details</summary>
          <p className="mt-2 text-xs leading-relaxed text-zinc-500">
            Links tagged with utm_source, utm_medium and utm_campaign, and the click ids ad platforms add to their links
            (fbclid, gclid, ttclid and friends), are saved with each visit. Meta's browser cookies (_fbp, _fbc) are kept too.
            At signup they are stored as "touches" on the person, which is how an ad click is matched to a customer.
          </p>
        </details>
      </Section>

      {/* Health */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-zinc-100">Is it working?</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat icon={Users} label="Visitors, 30 days" value={num(h.visitors_30d)} sub={`${num(h.pageviews_30d)} pages viewed`} />
          <Stat icon={Link2} label="People with history" value={num(h.contacts_with_history)} sub={`their browsing linked from ${num(h.linked_visitors)} ${h.linked_visitors === 1 ? "browser" : "browsers"}`} />
          <Stat icon={MousePointerClick} label="Arrivals recorded" value={num(h.touches)} sub={`${num(h.touches_with_click_ids)} came from an ad click`} />
          <Stat
            icon={Target}
            label="People credited"
            value={<>{num(h.credited)}<span className="text-base font-medium text-zinc-500"> of {num(people)}</span></>}
            target={{ value: creditedShare, label: `${num(h.uncredited)} not credited yet (came direct, or we can't tell)` }}
          />
        </div>
      </div>

      {/* Conversions API */}
      <Section
        title="Send results back to your ad platforms"
        icon={SendIcon}
        count={data.destinations.length}
        action={
          <span className="hidden sm:inline-flex">
            <Button size="sm" onClick={() => setAdding(true)} leftIcon={<Plus className="h-3.5 w-3.5" />} className="whitespace-nowrap">Add a destination</Button>
          </span>
        }
      >
        <p className="text-sm leading-relaxed text-zinc-400">
          The ad platforms learn from real signups and payments, so they find more people like your customers instead of people who only click.
        </p>
        {data.destinations.length > 0 && (
          <Button size="sm" className="mt-3 w-full sm:hidden" onClick={() => setAdding(true)} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add a destination</Button>
        )}
        {!data.configured && (
          <p className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">
            This CRM isn't linked to SyncSuite services yet, so nothing can be sent right now. Saved destinations start sending once it is.
          </p>
        )}
        <div className="mt-4 space-y-3">
          {data.destinations.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-800">
              <Empty
                icon={SendIcon}
                title="No destinations yet"
                action={<Button variant="secondary" size="sm" onClick={() => setAdding(true)} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add a destination</Button>}
              >
                Add your Meta pixel, a Google Ads conversion action or a TikTok event set. Connect an ads account in SyncSuite (Apps → Social & ads) and it shows up here.
              </Empty>
            </div>
          ) : (
            data.destinations.map((d) => (
              <DestinationRow
                key={d.id}
                d={d}
                onSaved={(next) => setDestinations((list) => list.map((x) => (x.id === next.id ? next : x)))}
                onRemoved={(id) => setDestinations((list) => list.filter((x) => x.id !== id))}
                onTested={() => void load()}
              />
            ))
          )}
        </div>
      </Section>

      {/* Send log */}
      <Section title="Recent sends" icon={Activity} count={data.sends.length}>
        <SendsLog sends={data.sends} />
      </Section>

      <AddDestinationModal
        open={adding}
        onClose={() => setAdding(false)}
        saved={data.destinations}
        onAdded={(d) => setDestinations((list) => [...list.filter((x) => x.id !== d.id), d])}
      />
    </div>
  );
}
